import React, { useEffect, useState } from 'react';
import { cn } from '~/lib/utils';
import type { Heading } from '~/types/blog';

interface TableOfContentsProps {
  headings: Heading[];
}

export function TableOfContents({ headings }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    const handleScroll = () => {
      // If near the top of the page, always highlight the first item
      if (window.scrollY < 200) {
        if (headings.length > 0) {
          const firstHeadingId = headings[0].id || headings[0].slug;
          setActiveId(firstHeadingId);
        }
        return;
      }

      const scrollPosition = window.scrollY + 100;
      let foundActive = false;

      for (let i = headings.length - 1; i >= 0; i--) {
        const heading = headings[i];
        const headingId = heading.id || heading.slug;
        const element = document.getElementById(headingId);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveId(headingId);
          foundActive = true;
          break;
        }
      }

      // If no heading is in view, highlight the first one by default
      if (!foundActive && headings.length > 0) {
        const firstHeadingId = headings[0].id || headings[0].slug;
        setActiveId(firstHeadingId);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [headings]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string, isFirstItem: boolean) => {
    e.preventDefault();

    // If it's the first item and the element doesn't exist (was removed), scroll to article top
    let element = document.getElementById(id);
    if (!element && isFirstItem) {
      element = document.getElementById('article-top');
    }

    if (element) {
      const offset = 80; // Account for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <nav>
      <ul className="space-y-2">
        {headings.map((heading, index) => {
          const headingId = heading.id || heading.slug;
          const isActive = activeId === headingId;
          const isFirstItem = index === 0;
          return (
            <li
              key={headingId}
              style={{ paddingLeft: `${(heading.level - 2) * 0.75}rem` }}
            >
              <a
                href={`#${headingId}`}
                onClick={(e) => handleClick(e, headingId, isFirstItem)}
                className={cn(
                  'block py-1.5 text-sm transition-all duration-200 border-l-2 pl-3 -ml-3',
                  isActive
                    ? 'text-yellow-500 font-semibold border-yellow-500'
                    : 'text-gray-400 hover:text-yellow-400 border-transparent hover:border-yellow-400/50'
                )}
              >
                {heading.text}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}